import { useEffect, useState, useCallback, useContext, useMemo } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { CSVLink } from "react-csv";
import { useNavigate } from "react-router-dom";
import Moment from "moment";
import { Skeleton } from "primereact/skeleton";
import AuthContext from "../../../auth/AuthContext";
import ReportService from "../../../services/ReportService";
import QuizService from "../../../services/QuizService";
import AlertService from "../../../services/AlertService";
import { Dropdown } from "primereact/dropdown";


const QuizReport = () => {
  const navigate = useNavigate();
  const authContext = useContext(AuthContext);

  const [results, setResults] = useState([]);
  const [filteredResults, setFilteredResults] = useState([]);
  const [selectedClients, setSelectedClients] = useState([]);
  const [isLoadingQuizzes, setIsLoadingQuizzes] = useState(false);
  const [isLoadingResults, setIsLoadingResults] = useState(false);
  const [quizId, setQuizId] = useState(0);
  const [quizzes, setQuizzes] = useState([]);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(0);

  const headers = [
    { label: "Display Name", key: "userDisplayName" },
    { label: "Email", key: "userEmail" },
    { label: "Quiz Name", key: "quizName" },
    { label: "Date", key: "attendedDate" },
    { label: "Correct Answers", key: "correctMCQAnswers" },
    { label: "Total Questions", key: "totalMCQQuestions" },
    { label: "Score", key: "mcqScore" },
  ];

  const supportAccess = authContext.getPermissions("report");

  useEffect(() => {
    if (
      supportAccess.filter((f) => f.access.includes(1)).length === 0 ||
      authContext.onGoingValidations
    ) {
      navigate("/");
    }
  }, [supportAccess, navigate, authContext]);

const loadQuizzes = useCallback(async (signal) => {
    setIsLoadingQuizzes(true);
    try {
      const data = await QuizService.GetList(authContext, signal); 

      if (signal?.aborted) return; 

      if (data.isSuccess) {
        const options = [{ value: 0, label: "All" }, ...data.data];
        setQuizzes(options);
      } else {
        AlertService.error(data.errorMessage);
      }
    } catch (err) {
      if (err.name === "AbortError") return;
    } finally {
      if (!signal?.aborted) setIsLoadingQuizzes(false); 
    }
  }, [authContext]);

const loadResults = useCallback(async (signal) => {
    setIsLoadingResults(true);
    try {
      const data = await ReportService.getQuizReport(
        quizId,
        authContext,
        signal
      );

      if (signal?.aborted) return; 

      if (data.isSuccess) {
        setResults(data.data);
        setFilteredResults(data.data);
        setCurrentPage(0);
      } else {
        AlertService.error(data.errorMessage);
      }
    } catch (err) {
      if (err.name === "AbortError") return; 
    } finally {
      if (!signal?.aborted) setIsLoadingResults(false); 
    }
  }, [quizId, authContext]);

useEffect(() => {
    const controller = new AbortController(); 
    loadQuizzes(controller.signal);
    return () => controller.abort(); 
  }, [loadQuizzes]);

useEffect(() => {
    const controller = new AbortController(); 
    loadResults(controller.signal); 
    return () => controller.abort(); 
  }, [loadResults, quizId]);

  const handleQuizFilter = (value) => {
    setQuizId(value);
    setCurrentPage(0);

    if (value === 0) {
      setFilteredResults(results);
    } else {
      const filtered = results.filter((item) => item.quizId === value);
      setFilteredResults(filtered);
    }
  };

  const paginatedClients = useMemo(() => {
    const startIndex = currentPage * rowsPerPage;
    const endIndex = startIndex + rowsPerPage;
    return filteredResults.slice(startIndex, endIndex);
  }, [filteredResults, currentPage, rowsPerPage]);

  const generatePageNumbers = () => {
    const totalPages = Math.ceil(filteredResults.length / rowsPerPage);
    const currentPageNumber = currentPage + 1;
    const pages = [];
    const maxVisiblePages = 3;

    if (totalPages <= 5) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      pages.push(1);
      if (currentPageNumber > maxVisiblePages + 1) pages.push("...");

      let start = Math.max(2, currentPageNumber - 1);
      let end = Math.min(totalPages - 1, currentPageNumber + 1);

      if (currentPageNumber <= maxVisiblePages) end = 4;
      else if (currentPageNumber > totalPages - maxVisiblePages)
        start = totalPages - 3;

      for (let i = start; i <= end; i++) {
        if (i > 1 && i < totalPages) pages.push(i);
      }

      if (currentPageNumber < totalPages - maxVisiblePages) pages.push("...");
      pages.push(totalPages);
    }
    return pages;
  };

  const skeletonData = Array.from({ length: rowsPerPage }, (_, i) => ({
    id: `skeleton-${i}`,
  }));
  const skeletonBody = () => <Skeleton width="90%" height="1.5rem" />;

  return (
    <div className="container-fluid custom-container position-relative mb-5">
      <div className="d-flex flex-column flex-md-row justify-content-end align-items-start align-items-md-center mb-3 mt-2">
        <div className="d-flex flex-wrap gap-3">
   <div className="flex-grow-1"  style={{ minWidth: "220px", maxWidth: "300px" }}>
  <Dropdown
    value={quizId}
    onChange={(e) => handleQuizFilter(Number(e.value))}
    options={quizzes}
    optionLabel="label"
    optionValue="value"
    placeholder="Select a Quiz"
    filter
    filterPlaceholder="Search quiz..."
    disabled={isLoadingQuizzes}
    className="w-100 custom-input-color report-dropdown"
    panelClassName="report-dropdown-panel"
    emptyMessage="No quizzes found"
    emptyFilterMessage="No matching quiz found"
  />
</div>

          <div>
            <CSVLink
              data={results}
              headers={headers}
              filename="Quiz-Results.csv"
              className={`btn btn-primary `}
            >
              <i className="pi pi-upload me-2"></i>Export
            </CSVLink>
          </div>
        </div>
      </div>

      <DataTable
        value={isLoadingResults ? skeletonData : paginatedClients}
        selection={selectedClients}
        onSelectionChange={(e) => setSelectedClients(e.value)}
        selectionMode="checkbox"
        emptyMessage={isLoadingResults ? "" : "No Data Found"}
        dataKey="id"
        responsiveLayout="scroll"
        className="custom-table"
         pt={{
    root: { style: { cursor: "default" } },
    bodyRow: { style: { cursor: "default" } },
    checkbox: { style: { cursor: "default" } }
  }}
      >
        <Column
          header="S. No."
          body={
            isLoadingResults
              ? skeletonBody
              : (_rowData, { rowIndex }) =>
                  currentPage * rowsPerPage + rowIndex + 1
          }
          style={{ width: "80px", textAlign: "center" }}
          bodyClassName="text-center"
          headerClassName="text-center"
        />
        <Column
          field="userDisplayName"
          header="Name"
          sortable
          body={
            isLoadingResults
              ? skeletonBody
              : (rowData) => rowData.userDisplayName
          }
        />
        <Column
          field="userEmail"
          header="Email"
          sortable
          body={
            isLoadingResults ? skeletonBody : (rowData) => rowData.userEmail
          }
        />
        <Column
          field="quizName"
          header="Quiz Name"
          sortable
          body={isLoadingResults ? skeletonBody : (rowData) => rowData.quizName}
        />
        <Column
          field="attendedDate"
          header="Date"
          body={
            isLoadingResults
              ? skeletonBody
              : (rowData) =>
                  rowData.attendedDate
                    ? Moment(rowData.attendedDate + "Z").format(
                        "DD/MM/YYYY hh:mm A"
                      )
                    : "-"
          }
        />
        <Column
          field="correctMCQAnswers"
          header="Correct Answers"
          bodyClassName="text-center"
          body={
            isLoadingResults
              ? skeletonBody
              : (rowData) => rowData.correctMCQAnswers
          }
        />
        <Column
          field="totalMCQQuestions"
          header="Total Questions"
          bodyClassName="text-center"
          body={
            isLoadingResults
              ? skeletonBody
              : (rowData) => rowData.totalMCQQuestions
          }
        />
        <Column
          field="mcqScore"
          header="Score"
          bodyClassName="text-center"
          body={isLoadingResults ? skeletonBody : (rowData) => rowData.mcqScore}
        />
      </DataTable>

      <div className="d-flex flex-column flex-md-row justify-content-md-between align-items-center mt-3 gap-2 paginator-wrapper">
        <div className="d-flex align-items-center">
          <label className="me-2 fw-bold">Show Rows:</label>
          {isLoadingResults ? (
            <Skeleton width="100px" height="38px" />
          ) : (
            <select
              className="form-select form-select-sm w-auto py-2 text-secondary custom-input-color"
              value={rowsPerPage}
              onChange={(e) => {
                setRowsPerPage(parseInt(e.target.value));
                setCurrentPage(0);
              }}
            >
              {[10, 20, 50].map((size) => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </select>
          )}
        </div>

        {isLoadingResults ? (
          <div className="d-flex gap-2">
            <Skeleton width="40px" height="40px" />
            <Skeleton width="40px" height="40px" />
            <Skeleton width="40px" height="40px" />
            <Skeleton width="40px" height="40px" />
            <Skeleton width="40px" height="40px" />
          </div>
        ) : (
          <div className="d-flex justify-content-center align-items-center gap-2">
            <button
              className="btn p-0 fs-5 pagination-item"
              style={{ width: "40px", height: "40px" }}
              onClick={() => setCurrentPage(Math.max(0, currentPage - 1))}
              disabled={currentPage === 0}
            >
              &lt;
            </button>

            {generatePageNumbers().map((page, index) =>
              page === "..." ? (
                <span key={index} className="mx-1 text-secondary">
                  ...
                </span>
              ) : (
                <button
                  key={index}
                  className={`btn p-0 pagination-item ${
                    currentPage + 1 === page ? "active-page" : ""
                  }`}
                  style={{ width: "40px", height: "40px" }}
                  onClick={() => setCurrentPage(page - 1)}
                >
                  {page}
                </button>
              )
            )}

            <button
              className="btn p-0 fs-5 pagination-item"
              style={{ width: "40px", height: "40px" }}
              onClick={() =>
                setCurrentPage((prev) =>
                  Math.min(
                    Math.ceil(filteredResults.length / rowsPerPage) - 1,
                    prev + 1
                  )
                )
              }
              disabled={
                currentPage >=
                Math.ceil(filteredResults.length / rowsPerPage) - 1
              }
            >
              &gt;
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default QuizReport;
